import { guidelinesApi, guidelineSourceExclusionApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const GUIDELINES_ACTION_TYPE = createNamedActions('guidelines', {
  GET_SOURCES: 'GET_SOURCES',
  GET_SOURCE_BY_ID: 'GET_SOURCE_BY_ID',
  CREATE_SOURCE: 'CREATE_SOURCE',
  UPDATE_SOURCE: 'UPDATE_SOURCE',
  DELETE_SOURCE: 'DELETE_SOURCE',
  GET_VERSIONS_BY_SOURCE: 'GET_VERSIONS_BY_SOURCE',
  CREATE_VERSION: 'CREATE_VERSION',
  DELETE_VERSION: 'DELETE_VERSION',
  SET_SELECTED_VERSION_ID: 'SET_SELECTED_VERSION_ID',
  GET_VERSION_TOC: 'GET_VERSION_TOC',
  GET_SECTION_BY_ID: 'GET_SECTION_BY_ID',
  CREATE_SECTION: 'CREATE_SECTION',
  UPDATE_SECTION: 'UPDATE_SECTION',
  DELETE_SECTION: 'DELETE_SECTION',
  SET_SELECTED_SECTION_ID: 'SET_SELECTED_SECTION_ID',
  GET_CHUNKS_BY_SECTION: 'GET_CHUNKS_BY_SECTION',
  REGENERATE_CHUNK_EMBEDDING: 'REGENERATE_CHUNK_EMBEDDING',
  RUN_GENERATE_MISSING_CHUNKS: 'RUN_GENERATE_MISSING_CHUNKS',
  RUN_REGENERATE_EMBEDDINGS: 'RUN_REGENERATE_EMBEDDINGS',
  TOGGLE_SOURCE_EXCLUSION: 'TOGGLE_SOURCE_EXCLUSION',
  RESET_GUIDELINES_STATE: 'RESET_GUIDELINES_STATE',
});

export const getSourcesAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await guidelinesApi.getSources();

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_SOURCES,
    payload: data,
  });
});

export const getSourceByIdAction = catchAsyncActionError(id => async dispatch => {
  const { data } = await guidelinesApi.getSourceById(id);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_SOURCE_BY_ID,
    payload: data,
  });
});

export const createSourceAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await guidelinesApi.createSource(body);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.CREATE_SOURCE,
    payload: data,
  });
});

export const updateSourceAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await guidelinesApi.updateSource(params);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.UPDATE_SOURCE,
    payload: data,
  });
});

export const deleteSourceAction = catchAsyncActionError(id => async dispatch => {
  await guidelinesApi.deleteSource(id);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.DELETE_SOURCE,
    payload: id,
  });
});

export const getVersionsBySourceAction = catchAsyncActionError(sourceId => async dispatch => {
  const { data } = await guidelinesApi.getVersionsBySource(sourceId);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_VERSIONS_BY_SOURCE,
    payload: { sourceId, versions: data },
  });
});

export const createVersionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await guidelinesApi.createVersion(params);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.CREATE_VERSION,
    payload: data,
  });
});

export const deleteVersionAction = catchAsyncActionError(({ id, sourceId }) => async dispatch => {
  await guidelinesApi.deleteVersion(id);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.DELETE_VERSION,
    payload: { id, sourceId },
  });
});

export const setSelectedVersionIdAction = ({ sourceId, versionId }) => ({
  type: GUIDELINES_ACTION_TYPE.SET_SELECTED_VERSION_ID,
  payload: { sourceId, versionId },
});

export const getVersionTocAction = catchAsyncActionError(versionId => async dispatch => {
  const { data } = await guidelinesApi.getVersionToc(versionId);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_VERSION_TOC,
    payload: { versionId, sections: data },
  });
});

export const getSectionByIdAction = catchAsyncActionError(sectionId => async dispatch => {
  const { data } = await guidelinesApi.getSectionById(sectionId);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_SECTION_BY_ID,
    payload: data,
  });
});

export const createSectionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await guidelinesApi.createSection(params);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.CREATE_SECTION,
    payload: data,
  });
});

export const updateSectionAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await guidelinesApi.updateSection(params);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.UPDATE_SECTION,
    payload: data,
  });
});

export const deleteSectionAction = catchAsyncActionError(({ id, versionId }) => async dispatch => {
  await guidelinesApi.deleteSection(id);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.DELETE_SECTION,
    payload: { id, versionId },
  });
});

export const setSelectedSectionIdAction = ({ versionId, sectionId }) => ({
  type: GUIDELINES_ACTION_TYPE.SET_SELECTED_SECTION_ID,
  payload: { versionId, sectionId },
});

export const getChunksBySectionAction = catchAsyncActionError(sectionId => async dispatch => {
  const { data } = await guidelinesApi.getChunksBySection(sectionId);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.GET_CHUNKS_BY_SECTION,
    payload: { sectionId, chunks: data },
  });
});

export const regenerateChunkEmbeddingAction = catchAsyncActionError(
  ({ chunkId, sectionId }) =>
    async dispatch => {
      const { data } = await guidelinesApi.regenerateChunkEmbedding(chunkId);

      return dispatch({
        type: GUIDELINES_ACTION_TYPE.REGENERATE_CHUNK_EMBEDDING,
        payload: { sectionId, chunk: data },
      });
    },
);

export const runGenerateMissingChunksAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await guidelinesApi.runGenerateMissingChunks(body);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.RUN_GENERATE_MISSING_CHUNKS,
    payload: data,
  });
});

export const runRegenerateEmbeddingsAction = catchAsyncActionError(body => async dispatch => {
  const { data } = await guidelinesApi.runRegenerateEmbeddings(body);

  return dispatch({
    type: GUIDELINES_ACTION_TYPE.RUN_REGENERATE_EMBEDDINGS,
    payload: data,
  });
});

export const excludeGuidelineSourceAction = catchAsyncActionError(
  ({ sourceId }) =>
    async dispatch => {
      await guidelineSourceExclusionApi.excludeSource({ sourceId });

      return dispatch({
        type: GUIDELINES_ACTION_TYPE.TOGGLE_SOURCE_EXCLUSION,
        payload: { sourceId, isExcluded: true },
      });
    },
);

export const includeGuidelineSourceAction = catchAsyncActionError(
  ({ sourceId }) =>
    async dispatch => {
      await guidelineSourceExclusionApi.includeSource({ sourceId });

      return dispatch({
        type: GUIDELINES_ACTION_TYPE.TOGGLE_SOURCE_EXCLUSION,
        payload: { sourceId, isExcluded: false },
      });
    },
);

export const resetGuidelinesStateAction = () => ({
  type: GUIDELINES_ACTION_TYPE.RESET_GUIDELINES_STATE,
});
